import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import api, { API } from '../utils/api';
import TemplateGallery from './TemplateGallery';

const QRForm = ({ onSubmit, loading, initialData, isEdit }) => {
  const { t } = useTranslation();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  const [formData, setFormData] = useState({
    name: '',
    type: 'url',
    destination: '',
    customization: {
      fgColor: '#0f172a',
      bgColor: '#ffffff',
      logoPath: null,
      size: 256,
    },
  });
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const handleTemplateSelect = (template) => {
    setFormData(prev => ({
      ...prev,
      type: template.suggestedType,
      destination: prev.destination || template.placeholder,
      customization: {
        ...prev.customization,
        fgColor: template.customization.fgColor,
        bgColor: template.customization.bgColor,
      },
    }));
    toast.success(`Template "${template.name}" applied`);
  };

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        type: initialData.type,
        destination: initialData.destination,
        customization: initialData.customization || {
          fgColor: '#0f172a',
          bgColor: '#ffffff',
          logoPath: null,
          size: 256,
        },
      });
    }
  }, [initialData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCustomizationChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      customization: { ...prev.customization, [field]: value },
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setUploadingLogo(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await api.post('/upload/logo', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      handleCustomizationChange('logoPath', response.data.path);
      setLogoFile(file);
      toast.success('Logo uploaded');
    } catch (error) {
      toast.error(t('error_occurred'));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onSubmit(formData);
    } catch (error) {
      // Error handled in parent
    }
  };

  const previewUrl = isEdit && initialData 
    ? `${backendUrl}/${initialData.uniqueSlug}` 
    : 'https://qr-cyprus.example.com/preview';

  return (
    <form onSubmit={handleSubmit} className="space-y-8" data-testid="qr-form">
      {!isEdit && (
        <div className="card p-6">
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-heading font-semibold text-primary mb-2">Start with a Template</h2>
              <p className="text-sm text-muted-foreground">Choose from our pre-designed templates or start from scratch</p>
            </div>
            <TemplateGallery onSelectTemplate={handleTemplateSelect} />
          </div>
        </div>
      )}
      
      <div className="grid lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div className="card p-6 space-y-4">
            <h2 className="text-xl font-heading font-semibold text-primary">{t('qr_name')}</h2>
            <div className="space-y-2">
              <Label htmlFor="name">{t('qr_name')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="My Campaign QR"
                required
                data-testid="qr-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('qr_type')}</Label>
              <RadioGroup value={formData.type} onValueChange={(value) => handleChange('type', value)}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="url" id="url" data-testid="qr-type-url" />
                  <Label htmlFor="url" className="font-normal cursor-pointer">{t('url_redirect')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="content" id="content" data-testid="qr-type-content" />
                  <Label htmlFor="content" className="font-normal cursor-pointer">{t('content_display')}</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="destination">{t('destination')}</Label>
              {formData.type === 'url' ? (
                <Input
                  id="destination"
                  type="url"
                  value={formData.destination}
                  onChange={(e) => handleChange('destination', e.target.value)}
                  placeholder="https://example.com"
                  required
                  data-testid="qr-destination-input"
                />
              ) : (
                <Textarea
                  id="destination"
                  value={formData.destination}
                  onChange={(e) => handleChange('destination', e.target.value)}
                  placeholder="Enter content to display..."
                  rows={4}
                  required
                  data-testid="qr-destination-textarea"
                />
              )}
            </div>
          </div>

          <div className="card p-6 space-y-4">
            <h2 className="text-xl font-heading font-semibold text-primary">{t('customize')}</h2>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fgColor">{t('foreground_color')}</Label>
                <div className="flex gap-2">
                  <Input
                    id="fgColor"
                    type="color"
                    value={formData.customization.fgColor}
                    onChange={(e) => handleCustomizationChange('fgColor', e.target.value)}
                    className="h-10 w-20"
                    data-testid="qr-fg-color-input"
                  />
                  <Input
                    type="text"
                    value={formData.customization.fgColor}
                    onChange={(e) => handleCustomizationChange('fgColor', e.target.value)}
                    className="flex-1"
                    data-testid="qr-fg-color-text"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="bgColor">{t('background_color')}</Label>
                <div className="flex gap-2">
                  <Input
                    id="bgColor"
                    type="color"
                    value={formData.customization.bgColor}
                    onChange={(e) => handleCustomizationChange('bgColor', e.target.value)}
                    className="h-10 w-20"
                    data-testid="qr-bg-color-input"
                  />
                  <Input
                    type="text"
                    value={formData.customization.bgColor}
                    onChange={(e) => handleCustomizationChange('bgColor', e.target.value)}
                    className="flex-1"
                    data-testid="qr-bg-color-text"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="logo">{t('upload_logo')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="logo"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="flex-1"
                  disabled={uploadingLogo}
                  data-testid="qr-logo-upload-input"
                />
                {uploadingLogo && <span className="text-sm text-muted-foreground">{t('loading')}</span>}
              </div>
              {formData.customization.logoPath && (
                <p className="text-xs text-green-600">Logo uploaded successfully</p>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="card p-6 space-y-4 sticky top-24">
            <h2 className="text-xl font-heading font-semibold text-primary">Preview</h2>
            <div className="bg-white p-8 rounded-lg border flex items-center justify-center" data-testid="qr-preview">
              <QRCodeSVG
                value={previewUrl}
                size={256}
                fgColor={formData.customization.fgColor}
                bgColor={formData.customization.bgColor}
                level="H"
                includeMargin={true}
              />
            </div>
            <p className="text-xs text-muted-foreground text-center">QR code will redirect to: {formData.destination || 'destination'}</p>
          </div>
        </div>
      </div>

      <div className="flex gap-4">
        <Button type="submit" className="btn-accent" disabled={loading} data-testid="qr-form-submit-btn">
          {loading ? t('loading') : (isEdit ? t('save') : t('create'))}
        </Button>
        <Button type="button" variant="outline" onClick={() => window.history.back()} data-testid="qr-form-cancel-btn">
          {t('cancel')}
        </Button>
      </div>
    </form>
  );
};

export default QRForm;
